import axios, { HttpStatusCode } from 'axios';
import dayjs from 'dayjs';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import union from 'lodash/union';
import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';

import { DashboardAPI } from '@/apis';
import * as AuthAPI from '@/apis/auth';
import { refreshCancelToken } from '@/apis/axios.utils';
import * as socialListeningApi from '@/apis/social-listening';
import { useListeningPermissions } from '@/app/socialListening/composables/useListeningPermissions';
import { useSocialListening } from '@/app/socialListening/composables/useSocialListening';
import {
  DEFAULT_KEYWORD_GROUPS,
  GRAPH_SCALE,
  KEYWORD_GROUP_TYPE,
  POST_VOLUME_GRAPH_OPTIONS,
  SOCIAL_LISTENING_CHANNEL_FILTER_DEFAULT,
  SOCIAL_LISTENING_MEDIATYPE_FILTER_DEFAULT,
  SOCIAL_LISTENING_ROUTE_NAMES,
  SOCIAL_LISTENING_SENTIMENT_FILTER_DEFAULT,
  SOCIAL_LISTENING_SENTIMENTS_OPTIONS,
  SOV_GRAPH_METRIC_OPTIONS,
} from '@/app/socialListening/constants';
import { CHANNEL_FILTER_OPTIONS } from '@/app/socialListening/constants/listeningFilters';
import { camelCaseObjDeep } from '@/app/socialListening/utils/api-request-utils';
import { getDateRangeByOffset } from '@/app/socialListening/utils/filter-utils';
import {
  buildMessageForParseTreeError,
  handleTextEditorErrorsUsingKeywordGroups,
  handleTextEditorErrorsUsingParseTree,
} from '@/app/socialListening/utils/keyword-composer-utils';
import { parseTreeToGroups } from '@/app/socialListening/utils/parsing';
import { useAuthStore } from '@/stores/auth';
import { useBooleanExpressionEditorStore } from '@/stores/boolean-expression-editor';
import { useNotificationStore } from '@/stores/notification';
import { useTrackingStore } from '@/stores/tracking';

export const SEARCH_BREAKDOWNS = Object.freeze({
  CREATOR: 'CREATOR',
  INDUSTRY: 'INDUSTRY',
  PLATFORM: 'PLATFORM',
  MEDIA_TYPE: 'MEDIA_TYPE',
  SENTIMENT: 'SENTIMENT',
  COUNTRY: 'COUNTRY',
  CITY: 'CITY',
  ADMINISTRATIVE_DIVISION: 'ADMINISTRATIVE_DIVISION',
  AGE: 'AGE',
  GENDER: 'GENDER',
});

export const useSocialListeningStore = defineStore('socialListening', {
  state: () => ({
    topicList: [],
    topKeywords: [],
    industries: [],
    visualTrends: [],
    visualTrendsRequestId: null,
    visualTrend: {},
    drawerVisualTrendsRequestId: null,
    drawerVisualTrends: [],
    trendGroups: [],
    selectedTopic: null,
    topicMonitors: [],
    pending: {
      topicComponentData: false,
      populateTopicStates: false,
      editTopic: false,
      topKeywords: true,
      postsByChannelTimeseries: false,
      postsBySentimentTimeseries: false,
      postsByMediaTypeTimeseries: false,
      totalPostsTimeseries: false,
      postVolume: false,
      postVolumeTimeSeries: false,
      postEngagement: false,
      postEngagementTimeSeries: false,
      getParseTree: false,
      updateSavedTopic: false,
      visualTrends: false,
      drawerVisualTrends: false,
      trendGroups: false,
      visualTrend: false,
      loadVisualData: true,
      visualDataRefinedResults: true,
      visualDataTotalResultsCount: false,
      webResultsPreview: true,
      webResults: true,
      topicMonitorCreateOrUpdate: false,
      topicMonitors: false,
      processExpression: false,
      fetchSuggestedTopics: false,
      topicStats: true,
      postById: false,
      topLocations: true,
      demographicsGender: true,
      demographicsAge: true,
    },
    errors: {
      trendGroups: false,
    },
    filters: null,
    cachedFilters: null,
    topicsUsage: null,
    wordcloud: {
      includeHashtags: true,
      includeKeywords: true,
    },
    topPostFilters: {
      channels: [],
      sentiments: [],
      mediaTypes: [],
      dateRange: '',
      sortBy: '',
      keyword: '',
      keywordTypes: [],
    },
    topPostsCustomDate: {
      label: '',
      start: null,
      end: null,
    },
    postsByChannelTimeseries: {},
    postsBySentimentTimeseries: {},
    postsByMediaTypeTimeseries: {},
    totalPostsTimeseries: {},
    postVolumeSelectedGraph: null,
    sovSelectedGraphMetric: SOV_GRAPH_METRIC_OPTIONS.POST_VOLUME.value,
    keywordGroups: null,
    teaserKeywordGroups: null,
    topicStats: {
      totalPosts: null,
      uniqueCreators: null,
      totalEngagements: null,
      averageEngagementsPerPost: null,
    },
    visualTrendPopupShow: false,
    popups: {
      editName: {
        show: false,
      },
      editIndustry: {
        show: false,
      },
      editCompetitor: {
        show: false,
      },
      editKeywords: {
        show: false,
      },
      createTopic: {
        show: false,
      },
      manageUsage: {
        show: false,
      },
      topicAlert: {
        show: false,
      },
      invalidTopicAlert: {
        show: false,
      },
      upgradeTier: {
        show: false,
      },
      upgradeTierAccess: {
        show: false,
      },
      upgradePlan: {
        show: false,
      },
      visualFilter: {
        show: false,
      },
    },
    postVolume: {},
    postVolumeTimeSeries: {},
    postEngagement: {},
    postEngagementTimeSeries: {},
    topicKeywordFilter: {
      rule: KEYWORD_GROUP_TYPE.INCLUDES,
      word: null,
    },
    unsavedFilters: {},
    createTopicInstanceId: null,
    mixpanelProps: {
      selectedCompetitorsHandles: [],
    },
    topLevelFiltersDisabled: false,
    unsavedKeywordChanges: false,
    topicCancelToken: {},
    keywordInputErrors: [],
    webResults: [],
    webResultsPreview: [],
    brandAccountIds: {},
    brandIndustries: [],
    postVolumeBrand: {},
    postVolumeTimeSeriesBrand: {},
    postEngagementBrand: {},
    postEngagementTimeSeriesBrand: {},
    organizationUsers: [],
    topicMonitorEvents: {},
    plainTextMode: false,
    quickSearchMode: false,
    keywordBooleanExpression: '',
    keywordBooleanExpressionErrors: [],
    keywordAndHashtagsEditorText: '',
    keywordTextEditorUsed: false,
    topLocations: [],
    demographicsGender: [],
    demographicsAge: [],
    disableAgeFilter: false,
  }),
  getters: {
    canSaveKeywordComposer: (state) => {
      if (state.plainTextMode || state.quickSearchMode) {
        return (
          !state.pending.getParseTree &&
          !state.pending.processExpression &&
          !state.keywordBooleanExpressionErrors.length
        );
      }
      return !state.keywordInputErrors.length;
    },
    hasExceededTopicLimit: (state) =>
      state?.topicsUsage?.topicsCreated >= state?.topicsUsage?.topicsLimit,
    hasExceededMentionLimit: (state) =>
      state?.topicsUsage?.mentionsAdded >= state?.topicsUsage?.mentionsRollingLimit,
    hasActiveTopicMonitors: (state) =>
      state?.topicMonitors?.some((topicMonitor) => topicMonitor.muted === false),
    hasTopicMonitors: (state) => state?.topicMonitors?.length,
    hasTopics: (state) => state.topicList?.length > 0,
    topKeywordsListStr: (state) => state?.topKeywords.map(({ keyword }) => keyword),
    keywordBooleanExpressionErrorMessages: (state) =>
      union(state?.keywordBooleanExpressionErrors.map(({ error }) => error)),
    showWebResultsPreview: (state) => state.filters?.websearch?.enabled,
  },
  actions: {
    resetWordcloudState() {
      this.wordcloud.includeKeywords = true;
      this.wordcloud.includeHashtags = true;
    },
    setQuickSearchMode(newValue) {
      this.quickSearchMode = newValue;
    },
    setKeywordTextEditorUsed() {
      this.keywordTextEditorUsed = true;
    },
    setTopPostFilters({
      channels,
      sentiments,
      mediaTypes,
      dateRange,
      sortBy,
      keyword,
      keywordTypes,
    }) {
      this.topPostFilters = {
        channels,
        sentiments,
        mediaTypes,
        dateRange,
        sortBy,
        keyword,
        keywordTypes,
      };
    },
    clearVisualTrends() {
      this.visualTrends = [];
    },
    enableTopLevelFilters() {
      this.topLevelFiltersDisabled = false;
    },
    disableTopLevelFilters() {
      this.topLevelFiltersDisabled = true;
    },
    addKeywordBooleanExpressionErrors(newErrors = []) {
      this.keywordBooleanExpressionErrors = [...this.keywordBooleanExpressionErrors, ...newErrors];
    },
    handleKeywordBooleanExpressionErrors(newGroups, parseTree, expression) {
      let errors = [];
      // using keyword groups
      const groups = newGroups || this.keywordGroups;
      if (groups?.length) {
        errors = errors.concat(handleTextEditorErrorsUsingKeywordGroups(groups));
        const booleanExpressionEditorStore = useBooleanExpressionEditorStore();
        booleanExpressionEditorStore.highlightDuplicateKeywords(groups);
      }
      // using parse tree
      if (parseTree) {
        const parseTreeErrors = handleTextEditorErrorsUsingParseTree(parseTree);
        if (parseTreeErrors?.length) {
          errors = errors.concat(parseTreeErrors);
        }
      }
      // using expression string
      if (expression) {
        const booleanEditorExpressionStore = useBooleanExpressionEditorStore();
        const expressionErrors =
          booleanEditorExpressionStore.handleTextEditorErrorsUsingExpression(expression);
        if (expressionErrors?.length) {
          errors = errors.concat(expressionErrors);
        }
      }
      this.addKeywordBooleanExpressionErrors(errors);
    },
    handleParseTreeError(error, hideErrorLine = false) {
      const booleanExpressionEditorStore = useBooleanExpressionEditorStore();
      booleanExpressionEditorStore.errorHighlightForSyntaxError(
        error,
        this.keywordBooleanExpression,
      );
      const { line } = error.location;
      const { unexpected, expected } = error.tokens;
      if (unexpected || expected.length) {
        const errorLine = hideErrorLine ? undefined : line;
        const formattedError = {
          error: buildMessageForParseTreeError(unexpected, expected, errorLine),
        };
        this.addKeywordBooleanExpressionErrors([formattedError]);
      }
    },
    async getParseTree(expressionInput) {
      this.pending.getParseTree = true;
      this.keywordBooleanExpressionErrors = [];
      const booleanExpressionEditorStore = useBooleanExpressionEditorStore();
      booleanExpressionEditorStore.resetTokenErrorMap();
      const { organizationId } = useSocialListening();
      try {
        // use the filters.keywordsAndHashtags if expressionInput is undefined
        const expression = expressionInput ?? this.filters?.keywordsAndHashtags;
        if (expression) {
          this.keywordBooleanExpression = expression;
          const res = await socialListeningApi.getParseTree({
            organizationId: organizationId.value,
            payload: { expression },
          });
          this.keywordGroups = parseTreeToGroups(res.data.data);
          if (this.plainTextMode || this.quickSearchMode) {
            await this.handleKeywordBooleanExpressionErrors(
              undefined,
              res?.data?.data,
              expressionInput,
            );
          }
        } else {
          this.keywordGroups = DEFAULT_KEYWORD_GROUPS;
          this.keywordBooleanExpression = '';
        }
      } catch (err) {
        const parseTreeError = err.response.data?.errors?.json?.expression?.error;
        if (err.response.status === 400 && parseTreeError) {
          this.handleParseTreeError(parseTreeError, this.quickSearchMode && !this.plainTextMode);
        }
        this.keywordBooleanExpression = '';
      } finally {
        this.pending.getParseTree = false;
      }
    },

    async getPostVolume({
      breakdown,
      topicId = null,
      brandCreatorIds = null,
      searchFilters = null,
      aggregationMetric = null,
      property = 'postVolume',
      teaserEndpoint = false,
    }) {
      const { organizationId } = useSocialListening();
      const useEstimates = this.getUseEstimatesParam(teaserEndpoint);
      const payload = {
        ...(breakdown ? { breakdownBy: breakdown } : {}),
        ...(searchFilters ? { searchFilters } : this.getBaseSearchParams()),
        ...(aggregationMetric ? { aggregationMetric } : {}),
      };
      if (topicId) {
        payload.topicId = topicId;
      }
      this[`${property}Brand`] = {};

      const cancelToken = refreshCancelToken(this, `${property}CancelToken`);
      this.pending[property] = true;
      try {
        const getFunction = teaserEndpoint
          ? socialListeningApi.getTeaserPostVolume
          : socialListeningApi.getPostVolume;
        const res = await getFunction(
          { organizationId: organizationId.value, payload, useEstimates },
          { cancelToken },
        );
        this[property] = res.data.data;
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        this[property] = {};
        this[`${property}Brand`] = {};
      }
      this.pending[property] = false;
    },
    async getSentimentTrendTimeSeries({
      searchFilters = {},
      scale = null,
      useEstimates = false,
      topicId = null,
    }) {
      const { organizationId } = useSocialListening();
      const payload = {
        breakdownBy: 'SENTIMENT',
        scale: scale || this.calculateScale(searchFilters),
        searchFilters,
      };
      if (topicId) {
        payload.topicId = topicId;
      }

      const results = await socialListeningApi.getTimeSeriesPostVolume({
        organizationId: organizationId.value,
        payload,
        useEstimates: { useEstimates },
      });
      return results.data.data;
    },
    async getNetSentimentTimeSeriesData({
      searchFilters = {},
      scale = null,
      useEstimates = false,
      topicId = null,
    }) {
      const { organizationId } = useSocialListening();
      const payload = {
        aggregationMetric: 'NET_SENTIMENT',
        breakdownBy: 'PLATFORM',
        scale: scale || this.calculateScale(searchFilters),
        searchFilters,
      };
      if (topicId) {
        payload.topicId = topicId;
      }

      const results = await socialListeningApi.getTimeSeriesPostVolume({
        organizationId: organizationId.value,
        payload,
        useEstimates: { useEstimates },
      });
      return results.data.data;
    },
    async getPostVolumeTimeSeries({
      brandCreatorIds = null,
      aggregationMetric = null,
      property = 'postVolume',
      isTeaser = false,
      teaserFilters = null,
      teaserEndpoint = false,
    }) {
      const { organizationId } = useSocialListening();
      const useEstimates = this.getUseEstimatesParam(teaserEndpoint);
      const breakdownBy = isTeaser ? 'INDUSTRY' : undefined;
      const payload = isTeaser
        ? {
            breakdownBy,
            scale: 'DAY',
            search_filters: teaserFilters,
          }
        : {
            ...(breakdownBy ? { breakdownBy } : {}),
            scale: this.calculateScale(),
            ...this.getBaseSearchParams(),
            ...(aggregationMetric ? { aggregationMetric } : {}),
          };
      this[`${property}TimeSeriesBrand`] = {};

      const cancelToken = refreshCancelToken(this, `${property}TSCancelToken`);
      this.pending[`${property}TimeSeries`] = true;
      try {
        const getFunction = teaserEndpoint
          ? socialListeningApi.getTeaserTimeSeriesPostVolume
          : socialListeningApi.getTimeSeriesPostVolume;
        const res = await getFunction(
          { organizationId: organizationId.value, payload, useEstimates },
          { cancelToken },
        );
        this[`${property}TimeSeries`] = res.data.data;
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        this[`${property}TimeSeries`] = {};
        this[`${property}TimeSeriesBrand`] = {};
      }
      this.pending[`${property}TimeSeries`] = false;
    },
    async getBrandIndustries(brandId) {
      this.brandIndustries = [];
      const brandAccountIds = this.brandAccountIds[brandId].map(
        (sourceAndId) => sourceAndId.split(':')[1],
      );
      if (brandAccountIds.length) {
        const promises = brandAccountIds.map((id) => DashboardAPI.getIndustriesBySourceAccount(id));
        let brandIndustryData = [];
        await Promise.allSettled(promises).then((results) => {
          results.forEach((result) => {
            if (result.value) {
              brandIndustryData = [...brandIndustryData, ...result.value.data];
            }
          });
        });
        const { searchFilters } = this.getBaseSearchParams();
        const brandIndustryNames = brandIndustryData.map((i) => i.name);
        this.brandIndustries = this.industries.filter(
          (i) => searchFilters.industryIds.includes(i.id) && brandIndustryNames.includes(i.name),
        );
      }
    },
    async fetchVisualTrends(socketId) {
      // Fetch visual trends for topics page
      const requestId = uuidv4();

      const { topicId, searchFilters } = this.getBaseSearchParams();
      this.visualTrendsRequestId = requestId;
      await this.getVisualTrends({
        requestId,
        socketId,
        searchBody: { filters: searchFilters },
        topicId,
      });
    },
    async getVisualTrendsV2(socketId, requestId, filters, topicId = undefined) {
      const { organizationId } = useSocialListening();
      const payload = {
        searchBody: { filters },
        requestId,
        socketId,
      };
      if (topicId) {
        payload.topicId = topicId;
      }

      const res = await socialListeningApi.getVisualTrends({
        organizationId: organizationId.value,
        payload,
      });
      return res.data.data;
    },
    async getDrawerVisualTrends(socketId, filters, topicId = null) {
      const requestId = uuidv4();
      this.drawerVisualTrendsRequestId = requestId;

      // We want to keep the pending state until the websocket comes in, so only set false
      // in the case of errors or cached results
      this.drawerVisualTrends = [];
      this.pending.drawerVisualTrends = true;
      try {
        const results = await this.getVisualTrendsV2(socketId, requestId, filters, topicId);
        this.drawerVisualTrends = results;
        if (results.length > 0) {
          this.pending.drawerVisualTrends = false;
        }
      } catch {
        this.pending.drawerVisualTrends = false;
      }
    },
    async updateSavedTopic(payload) {
      const { organizationId } = useSocialListening();

      try {
        this.pending.updateSavedTopic = true;
        await socialListeningApi.updateTopic(
          { organizationId: organizationId.value, topicId: this.selectedTopic.id },
          payload,
        );
        this.pending.updateSavedTopic = false;
        return true;
      } catch {
        this.pending.updateSavedTopic = false;
        const notificationStore = useNotificationStore();
        notificationStore.setToast({
          message: 'Update failed to save. Please refresh and try again.',
          type: 'error',
        });
        return false;
      }
    },
    formatDateOptions(onOrAfter, onOrBefore, offset) {
      let startDate = dayjs(onOrAfter);
      let endDate = dayjs(onOrBefore);
      if (dayjs().isSame(endDate, 'day') && endDate.diff(startDate, 'day') === 1 && offset === 1) {
        const currentHour = dayjs().hour();
        startDate = startDate.set('hour', currentHour).set('minute', 0).set('second', 0);
        endDate = endDate.set('hour', currentHour).set('minute', 0).set('second', 0);
      } else {
        startDate = startDate.startOf('day');
        endDate = endDate.endOf('day');
      }
      return {
        startDate: startDate.format('YYYY-MM-DD HH:mm:ss'),
        endDate: endDate.format('YYYY-MM-DD HH:mm:ss'),
      };
    },
    async updateSourceCreated(onOrAfter, onOrBefore, offset, updateTopic = true) {
      const { startDate, endDate } = this.formatDateOptions(onOrAfter, onOrBefore, offset);

      let sourceCreated =
        onOrAfter || onOrBefore
          ? {
              ...(onOrAfter
                ? {
                    onOrAfter: startDate,
                  }
                : {}),
              ...(onOrBefore
                ? {
                    onOrBefore: endDate,
                  }
                : {}),
            }
          : undefined;

      const sourceCreatedUpdate = offset ? { rollingDateRangeOffset: offset } : sourceCreated;

      const updated = updateTopic
        ? await this.updateSavedTopic({
            searchBody: { filters: { ...this.filters, sourceCreated: sourceCreatedUpdate } },
          })
        : true;
      if (updated) {
        sourceCreated =
          sourceCreated && offset
            ? { ...sourceCreated, rollingDateRangeOffset: offset }
            : sourceCreated;
        this.filters = { ...this.filters, sourceCreated };
      }
    },
    async updateFiltersLocally({
      mediaTypes,
      sources,
      sentiment,
      sourceCreated,
      visualData,
      industryIds,
      keywordsAndHashtags,
      topicId,
    }) {
      let sentimentUpdated = {};
      let mediaTypesUpdated;
      let sourcesUpdated;
      if (sentiment && !isEqual(SOCIAL_LISTENING_SENTIMENT_FILTER_DEFAULT, sentiment)) {
        Object.values(SOCIAL_LISTENING_SENTIMENTS_OPTIONS).forEach((s) => {
          Object.assign(sentimentUpdated, { [s.field]: sentiment.includes(s.value) });
        });
      } else {
        sentimentUpdated = null;
      }
      if (!isEqual(SOCIAL_LISTENING_MEDIATYPE_FILTER_DEFAULT, mediaTypes)) {
        mediaTypesUpdated = { includes: mediaTypes };
      }
      if (!isEqual(SOCIAL_LISTENING_CHANNEL_FILTER_DEFAULT, sources)) {
        sourcesUpdated = sources;
      }
      this.filters = {
        ...(this.filters || {}),
        ...(sourcesUpdated ? { sources: sourcesUpdated } : {}),
        ...(mediaTypesUpdated ? { mediaTypes: mediaTypesUpdated } : {}),
        ...(sentimentUpdated ? { sentiment: sentimentUpdated } : {}),
        ...(sourceCreated ? { sourceCreated } : {}),
        ...(visualData ? { visualData } : {}),
        ...(industryIds ? { industryIds } : {}),
        ...(keywordsAndHashtags ? { keywordsAndHashtags } : {}),
        ...(topicId ? { topicId } : {}),
      };
    },
    async updateTopicName(name) {
      const updated = await this.updateSavedTopic({ name });
      if (updated && this.selectedTopic) {
        this.selectedTopic.name = name;
        const index = this.topicList.findIndex((t) => t.id === this.selectedTopic.id);
        this.topicList[index].name = name;
      }
    },
    getBaseSearchParams() {
      return {
        topicId: this.selectedTopic?.id,
        searchFilters: {
          ...this.filters,
          ...this.unsavedFilters,
        },
      };
    },
    canUseEstimates(teaserEndpoint = false) {
      const isTrendsRoute =
        window.location.pathname.endsWith('/trends') ||
        window.location.pathname.includes('/trends/');
      const trackingStore = useTrackingStore();
      const viewTrends =
        trackingStore.currentRoute?.name === SOCIAL_LISTENING_ROUTE_NAMES.TRENDS ||
        trackingStore.currentRoute?.name === SOCIAL_LISTENING_ROUTE_NAMES.TRENDS_REPORT ||
        isTrendsRoute;
      return !viewTrends && !teaserEndpoint;
    },
    getUseEstimatesParam(teaserEndpoint = false) {
      return { useEstimates: this.canUseEstimates(teaserEndpoint) };
    },
    getScaleParam(teaserEndpoint = false) {
      return this.canUseEstimates(teaserEndpoint) ? { scale: this.calculateScale() } : {};
    },
    async updateFilters({ mediaTypes, sources, visualData, sentiment }, updateTopic = true) {
      let mediaTypesArray = null;
      let newSentiment = {};
      // cachedFilters saves filters as objects, so we want to send the correct values when using cachedFilters
      if (sentiment && sentiment instanceof Array) {
        Object.values(SOCIAL_LISTENING_SENTIMENTS_OPTIONS).forEach((s) => {
          newSentiment[s.field] = sentiment.includes(s.value);
        });
      } else {
        newSentiment = sentiment;
      }
      if (mediaTypes && !(mediaTypes instanceof Array)) {
        mediaTypesArray = [...mediaTypes.includes];
      }
      const newFilters = {
        ...this.filters,
        sources: sources ?? undefined,
        mediaTypes: mediaTypes ? { includes: mediaTypesArray ?? mediaTypes } : undefined,
        visualData:
          visualData?.includes?.length || visualData?.doesNotInclude?.length
            ? visualData
            : undefined,
        sentiment: !isEmpty(sentiment) ? newSentiment : undefined,
      };
      if (!isEqual(this.filters, newFilters)) {
        const updated = updateTopic
          ? await this.updateSavedTopic({ searchBody: { filters: newFilters } })
          : true;
        if (updated) {
          this.filters = newFilters;
        }
      }
    },
    async updateTopicFilters(overrideFilters = {}, updateTopic = true) {
      const { sourceAndCreatorIds, industryIds, websearch, keywordsAndHashtags, ...filterData } =
        overrideFilters ?? {};
      const newFilters = omitBy({ ...this.filters, ...filterData }, isEmpty);
      if (!isEqual(omitBy(this.filters, isEmpty), newFilters)) {
        const updated = updateTopic
          ? await this.updateSavedTopic({ searchBody: { filters: newFilters } })
          : true;
        if (updated) {
          const offset = this.filters?.sourceCreated?.rollingDateRangeOffset;
          if (offset) {
            const { start, end } = getDateRangeByOffset(offset);
            this.filters.sourceCreated = {
              onOrAfter: start,
              onOrBefore: end,
              rollingDateRangeOffset: offset,
            };
          }
          this.filters = newFilters;
        }
      }
    },
    async getTopKeywords(payload, teaserEndpoint = false) {
      this.pending.topKeywords = true;
      const cancelToken = refreshCancelToken(this, 'topKeywordsCancelToken');
      const { organizationId } = useSocialListening();
      const useEstimates = this.getUseEstimatesParam(teaserEndpoint);
      try {
        const getFunction = teaserEndpoint
          ? socialListeningApi.getTeaserTopKeywords
          : socialListeningApi.getTopKeywords;
        const res = await getFunction(
          { organizationId: organizationId.value, payload, useEstimates },
          { cancelToken },
        );
        this.topKeywords = res.data.data;
        this.pending.topKeywords = false;
      } catch (error) {
        if (axios.isCancel(error)) {
          // show loading state until newer request finishes
          return;
        }
        this.topKeywords = [];
        this.pending.topKeywords = false;
      }
    },
    async getPostById(sourceId, source, teaserEndpoint = false) {
      const { organizationId } = useSocialListening();
      const payload = teaserEndpoint
        ? {
            filters: {
              source_and_ids: [`${source}:${sourceId}`],
              industry_ids: this.industries.map((i) => i.id),
              source_created: {
                on_or_after: dayjs()
                  .subtract(29, 'day')
                  .startOf('day')
                  .format('YYYY-MM-DDTHH:mm:ss'),
                on_or_before: dayjs().endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
              },
            },
          }
        : {
            filters: {
              source_and_ids: [`${source}:${sourceId}`],
            },
          };
      try {
        this.pending.postById = true;
        const getFunction = teaserEndpoint
          ? socialListeningApi.getTeaserSearchResults
          : socialListeningApi.getSearchResults;
        const res = await getFunction({ organizationId: organizationId.value, payload });
        let media = null;
        const mediaItems = res.data.data;
        if (mediaItems) {
          media = mediaItems[0];
        }
        this.pending.postById = false;
        return media;
      } catch {
        this.pending.postById = false;
        return null;
      }
    },
    async fetchIndustries() {
      try {
        const response = await DashboardAPI.getIndustries(false);
        this.industries = response?.data;
      } catch {
        this.industries = [];
      }
    },
    togglePopup(name, value) {
      if (!name) {
        return;
      }
      const newPopupValue = value ?? !this.popups[name].show;
      if (name === 'createTopic') {
        // set instance id fire off mixpanel event
        this.createTopicInstanceId = newPopupValue ? uuidv4() : null;
      }
      this.popups[name].show = newPopupValue;
    },
    async getTimeSeriesPostVolume(
      payload,
      graphType,
      useEstimatesParam,
      cancelToken,
      teaserEndpoint = false,
    ) {
      this.setPostVolumePending(true, graphType);
      this.setPostVolumeData({}, graphType);
      const useEstimates = useEstimatesParam ?? this.getUseEstimatesParam(teaserEndpoint);
      try {
        const { organizationId } = useSocialListening();
        const getFunction = teaserEndpoint
          ? socialListeningApi.getTeaserTimeSeriesPostVolume
          : socialListeningApi.getTimeSeriesPostVolume;
        const res = await getFunction(
          {
            organizationId: organizationId.value,
            payload,
            useEstimates,
          },
          { cancelToken },
        );
        const data = res.data.data;
        this.setPostVolumeData(data, graphType);
      } catch (error) {
        if (!axios.isCancel(error)) {
          this.setPostVolumeData({}, graphType);
        }
      } finally {
        this.setPostVolumePending(false, graphType);
      }
    },
    clearPostVolumeTimeSeriesData() {
      this.postsByChannelTimeseries = {};
      this.postsBySentimentTimeseries = {};
      this.postsByMediaTypeTimeseries = {};
      this.totalPostsTimeseries = {};
    },
    setPostVolumeData(data, graphType) {
      if (graphType === POST_VOLUME_GRAPH_OPTIONS.POSTS_BY_CHANNEL.value) {
        this.postsByChannelTimeseries = data;
      } else if (graphType === POST_VOLUME_GRAPH_OPTIONS.POSTS_BY_SENTIMENT.value) {
        this.postsBySentimentTimeseries = data;
      } else if (graphType === POST_VOLUME_GRAPH_OPTIONS.POSTS_BY_MEDIA_TYPE.value) {
        this.postsByMediaTypeTimeseries = data;
      } else if (graphType === POST_VOLUME_GRAPH_OPTIONS.TOTAL_POSTS.value) {
        this.totalPostsTimeseries = data;
      }
    },
    setPostVolumePending(value, graphType) {
      if (graphType === POST_VOLUME_GRAPH_OPTIONS.POSTS_BY_CHANNEL.value) {
        this.pending.postsByChannelTimeseries = value;
      } else if (graphType === POST_VOLUME_GRAPH_OPTIONS.POSTS_BY_SENTIMENT.value) {
        this.pending.postsBySentimentTimeseries = value;
      } else if (graphType === POST_VOLUME_GRAPH_OPTIONS.POSTS_BY_MEDIA_TYPE.value) {
        this.pending.postsByMediaTypeTimeseries = value;
      } else if (graphType === POST_VOLUME_GRAPH_OPTIONS.TOTAL_POSTS.value) {
        this.pending.totalPostsTimeseries = value;
      }
    },
    async getVisualTrends(payload, teaserEndpoint = false) {
      const { organizationId } = useSocialListening();

      try {
        this.visualTrends = [];
        this.pending.visualTrends = true;
        const getFunction = teaserEndpoint
          ? socialListeningApi.getTeaserVisualTrends
          : socialListeningApi.getVisualTrends;
        const res = await getFunction({ organizationId: organizationId.value, payload });
        if (res.status === HttpStatusCode.Ok) {
          this.visualTrends = res.data?.data || [];
          this.pending.visualTrends = false;
        } else {
          this.visualTrendsRequestId = payload?.requestId || null;
        }
      } catch {
        this.visualTrends = [];
        this.pending.visualTrends = false;
      }
    },
    onClusterTaskNotification(visualTrendsData) {
      // If data is being sent back from an old request, we want to ignore it
      const trends = visualTrendsData?.buckets || [];
      if (visualTrendsData.request_id === this.visualTrendsRequestId) {
        this.visualTrends = trends.map((trend) => camelCaseObjDeep(trend));
      } else if (visualTrendsData.request_id === this.drawerVisualTrendsRequestId) {
        this.drawerVisualTrends = trends.map((trend) => camelCaseObjDeep(trend));
      }
      this.pending.visualTrends = false;
      this.pending.drawerVisualTrends = false;
    },
    async getVisualTrendWithOffset(trendId, offset, limit, teaserEndpoint = false) {
      const { organizationId } = useSocialListening();
      try {
        this.pending.visualTrend = true;
        const getFunction = teaserEndpoint
          ? socialListeningApi.getTeaserTrendGroup
          : socialListeningApi.getTrendGroup;
        const res = await getFunction({
          organizationId: organizationId.value,
          trendId,
          offset,
          limit,
        });
        if (offset) {
          const oldContent = [...this.visualTrend.content];
          const newTrend = res.data.data;
          newTrend.content = oldContent.concat(newTrend.content);
          this.visualTrend = newTrend;
        } else {
          this.visualTrend = res.data.data;
        }
      } catch {
        this.visualTrend = {};
      } finally {
        this.pending.visualTrend = false;
      }
    },
    updateUnsavedFilters(filters = {}) {
      this.unsavedFilters = filters;
    },
    updateKeywordFilter(filters = {}) {
      this.topicKeywordFilter = {
        ...this.topicKeywordFilter,
        ...filters,
      };
    },
    clearUnsavedTopicKeywordFilter() {
      this.updateUnsavedFilters();
      this.updateKeywordFilter({
        word: null,
        rule: KEYWORD_GROUP_TYPE.INCLUDES,
      });
    },
    logMixpanelEvent(event, properties) {
      const trackingStore = useTrackingStore();
      trackingStore.track(event, properties);
    },
    clearOverviewData(clearPageLevelDataOnly = false) {
      this.visualTrends = [];
      if (!clearPageLevelDataOnly) {
        this.postVolume = {};
        this.clearPostVolumeTimeSeriesData();
      }
    },
    calculateScale(filters = this.filters) {
      if (!filters?.sourceCreated) return GRAPH_SCALE.DAY;
      const start = dayjs(filters.sourceCreated.onOrAfter);
      const end = dayjs(filters.sourceCreated.onOrBefore);
      const diff = end.diff(start, 'day');
      if (diff <= 1) {
        return GRAPH_SCALE.HOUR;
      }
      if (diff > 1 && diff <= 30) {
        return GRAPH_SCALE.DAY;
      }
      if (diff > 30 && diff <= 90) {
        return GRAPH_SCALE.WEEK;
      }
      return GRAPH_SCALE.MONTH;
    },
    flattenCountryCounts(jsonData) {
      const result = {};

      if (!jsonData || !jsonData.data) return [];

      Object.entries(jsonData.data).forEach(([, entry]) => {
        const breakdownData = entry?.breakdown?.data || [];

        breakdownData.forEach(({ group, count }) => {
          if (group && typeof count === 'number') {
            result[group] = (result[group] || 0) + count;
          }
        });
      });

      // Sort by count descending
      return Object.entries(result)
        .map(([location, count]) => ({ location, count }))
        .sort((a, b) => b.count - a.count);
    },
    flattenDemographicsGenderCounts(jsonData) {
      const result = { male: 0, female: 0, other: 0 };

      if (!jsonData || !jsonData.data || jsonData.data.count === 0) return [];

      const breakdownData = jsonData.data.breakdown?.data || [];

      breakdownData.forEach(({ group, count }) => {
        if (group && typeof count === 'number') {
          result[group] = (result[group] || 0) + count;
        }
      });

      // Sort by male, female, other always
      const order = ['male', 'female', 'other'];
      return Object.entries(result)
        .map(([gender, count]) => ({ gender, count }))
        .sort((a, b) => order.indexOf(a.gender) - order.indexOf(b.gender));
    },
    flattenDemographicsAgeCounts(jsonData) {
      const result = {};

      if (!jsonData || !jsonData.data || jsonData.data.count === 0) return [];

      const breakdownData = jsonData.data.breakdown?.data || [];
      breakdownData.forEach(({ group, count, secondaryBreakdown }) => {
        if (group && typeof count === 'number') {
          result[group] = {
            all: (result[group]?.all || 0) + count,
            male: result[group]?.male || 0,
            female: result[group]?.female || 0,
            other: result[group]?.other || 0,
          };
        }
        if (secondaryBreakdown && Array.isArray(secondaryBreakdown.data)) {
          secondaryBreakdown.data.forEach(({ group: secGroup, count: secCount }) => {
            if (result[group] && typeof secCount === 'number') {
              result[group][secGroup] = (result[group][secGroup] || 0) + secCount;
            }
          });
        }
      });

      return result;
    },
    clearWebResults() {
      this.webResults = [];
      this.webResultsPreview = [];
    },
    async getOrganizationUsers() {
      const authStore = useAuthStore();
      const { organizationId } = useSocialListening();
      const orgAccessibleBrands = Object.values(authStore.identity?.brands ?? {})
        .filter((brand) => brand.organizationId === organizationId.value)
        .map((brand) => brand.id);
      const responses = await Promise.allSettled(
        orgAccessibleBrands.map((brandId) => AuthAPI.getBrandUsers({ brandId, limit: 1000 })),
      );
      const allUsers = flatMap(responses, (response) => response?.value?.data?.data || []);
      const uniqueUsers = new Set();
      this.organizationUsers = allUsers.filter((user) => {
        const isDuplicate = uniqueUsers.has(user.id);
        uniqueUsers.add(user.id);
        return !isDuplicate;
      });
    },
    getChannels(channels = []) {
      const { canAccessYoutubeCompliance } = useListeningPermissions();

      let filteredChannels = channels;
      if (!canAccessYoutubeCompliance.value) {
        filteredChannels = filteredChannels.filter(
          (c) => c !== CHANNEL_FILTER_OPTIONS.YOUTUBE.value,
        );
      }
      return filteredChannels;
    },
    async getTopLocations(topicId, breakdownBy, searchFilters) {
      this.pending.topLocations = true;
      this.topLocations = [];

      const cancelToken = refreshCancelToken(this, 'topLocationsCancelToken');
      const { organizationId } = useSocialListening();
      const payload = {
        topicId,
        breakdownBy,
        searchFilters,
        useEstimates: true,
        secondaryBreakdownBy: SEARCH_BREAKDOWNS.PLATFORM,
        scale: this.calculateScale(searchFilters),
      };

      try {
        const response = await socialListeningApi.getTimeSeriesPostVolume(
          { organizationId: organizationId.value, payload },
          { cancelToken },
        );
        this.topLocations = this.flattenCountryCounts(response.data);
      } finally {
        this.pending.topLocations = false;
      }
    },
    async getDemographicsByGender(topicId, searchFilters) {
      this.pending.demographicsGender = true;
      this.demographicsGender = [];

      const cancelToken = refreshCancelToken(this, 'demographicsGenderCancelToken');
      const { organizationId } = useSocialListening();
      const payload = {
        topicId,
        breakdownBy: SEARCH_BREAKDOWNS.GENDER,
        searchFilters,
        useEstimates: true,
        secondaryBreakdownBy: SEARCH_BREAKDOWNS.PLATFORM,
      };

      try {
        const response = await socialListeningApi.getPostVolume(
          { organizationId: organizationId.value, payload },
          { cancelToken },
        );
        this.demographicsGender = this.flattenDemographicsGenderCounts(response.data);
      } finally {
        this.pending.demographicsGender = false;
      }
    },
    async getDemographicsByAge(topicId, searchFilters) {
      this.pending.demographicsAge = true;
      this.demographicsAge = [];

      const cancelToken = refreshCancelToken(this, 'demographicsAgeCancelToken');
      const { organizationId } = useSocialListening();
      const payload = {
        topicId,
        breakdownBy: SEARCH_BREAKDOWNS.AGE,
        searchFilters,
        useEstimates: true,
      };

      try {
        const firstResponse = await socialListeningApi.getPostVolume(
          {
            organizationId: organizationId.value,
            payload: {
              ...payload,
              secondaryBreakdownBy: SEARCH_BREAKDOWNS.GENDER,
            },
          },
          { cancelToken },
        );
        const firstData = this.flattenDemographicsAgeCounts(firstResponse.data);
        if (!isEmpty(firstData)) {
          this.demographicsAge = firstData;
          this.disableAgeFilter = false;
        } else {
          // Because of PII violations, we want to at least fetch data for all ages,
          // without gender breakdown
          this.disableAgeFilter = true;
          const fallbackResponse = await socialListeningApi.getPostVolume(
            { organizationId: organizationId.value, payload },
            { cancelToken },
          );
          this.demographicsAge = this.flattenDemographicsAgeCounts(fallbackResponse.data);
        }
      } finally {
        this.pending.demographicsAge = false;
      }
    },
  },
});
